import React, { useState } from 'react';
import { Col, Form, Input, Modal, notification, Row, Checkbox } from 'antd';
import Signature from 'mixins/ui/button-signature';
import Button from 'mixins/ui/button/button';
import service from '../../../services/account';
import { auth, confirmEmail, getDataFromEcp, sendEmailConfirmKey } from '../actions';
import {edsResultCheck, getLangValue, localize} from 'mixins/helpers';
import { STATUS, TProps } from '../block-auth.type';
import RegVerificationModal from './reg-verification-modal';

const SignUpUser = ({
                        getToken,
                        sessionToken,
                        setSignType,
                        translations,
                        language,
                    }: TProps) => {
    const [status, setStatus] = useState<STATUS>('INITIAL');
    const [signing, setSigning] = useState(false);
    const [openRegVerification, setOpenRegVerification] = useState(false);
    const [signedData, setSignedData] = useState<string>();
    const [agreementChecked, setAgreementChecked] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);

    const [form] = Form.useForm();
    const [formRegVerification] = Form.useForm();

    const checkUserByEds = (xmlSigned: string) => {
        setSigning(true);

        service
            .checkUserByEds(xmlSigned)
            .then((value) => {
                form.setFieldValue('login', value.data);

                setStatus('CHECKED');
                setSignedData(xmlSigned);
            })
            .finally(() => setSigning(false));
    };

    const signUp = (values: any) => {
        setSigning(true);

        auth({
            action: 'signup',
            signedData: btoa(unescape(encodeURIComponent(signedData))),
            ...values,
        })
            .then((value) => {
                if (value.data.key) {
                    notification.success({message: localize('block-auth-successfully-registered', translations)});

                    setSignType('LOGIN');
                }
            })
            .finally(() => setSigning(false));
    };

    function onFinish(values: any) {
        // email verification
        getDataFromEcp(btoa(unescape(encodeURIComponent(signedData)))).then(({ data }) => {
            const dataObj = typeof data === 'string' ? JSON.parse(data) : data;
            const iin = edsResultCheck(dataObj, translations);
            if (iin) {
                sendEmailConfirmKey(values.email, getLangValue(language).toUpperCase()).then(() => {
                    setOpenRegVerification(true);
                });
            }
        });
    }

    const handleAgreementChange = (e) => {
        setAgreementChecked(e.target.checked);
    };

    const handleAccept = () => {
        setAgreementChecked(true);
        handleModalClose();
    };

    const openModalLink = () => {
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
    };

    const closeRegVerificationModal = () => {
        setOpenRegVerification(false);
        formRegVerification.resetFields();
    };

    const handleRegVerification = () => {
        formRegVerification.validateFields().then(() => {
            const { confirmCode } = formRegVerification.getFieldsValue();

            confirmEmail(form.getFieldValue('email'), confirmCode).then(() => {
                closeRegVerificationModal();
                signUp(form.getFieldsValue());
            });
        });
    };

    return (
        <>
            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
            >
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <div className="block-auth__title">
                            {localize('block-auth-title', translations)}
                        </div>
                    </Col>
                    {status === 'INITIAL' ? (
                        <>
                            <Col span={24}>
                                <Signature
                                    onGetXml={() => getToken()}
                                    onSubmit={checkUserByEds}
                                    loading={signing}
                                    icon
                                    translations={translations}
                                />
                            </Col>
                            <Col
                                span={24}
                                className="text-secondary"
                            >
                                {localize('block-auth-select-an-eds', translations)}
                            </Col>
                            <Col span={24}>
                                <div className="text-secondary">
                                    {localize('block-auth-have-account-log-in-1', translations)},{' '}
                                    <a
                                        href="#"
                                        onClick={() => setSignType('LOGIN')}
                                    >
                                        {localize('block-auth-have-account-log-in-2', translations)}
                                    </a>
                                </div>
                            </Col>
                        </>
                    ) : status === 'CHECKED' ? (
                        <Col span={24}>
                            <Form.Item name="login">
                                <Input
                                    placeholder={localize('block-auth-login', translations)}
                                    disabled={true}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[
                                    {
                                        required: true,
                                        message: localize(
                                            'block-auth-required-field',
                                            translations
                                        ),
                                    },
                                    {
                                        pattern:
                                            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                                        message: localize(
                                            'block-auth-incorrect-password-format',
                                            translations
                                        ),
                                    },
                                ]}
                            >
                                <Input.Password
                                    placeholder={localize('block-auth-password', translations)}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Checkbox
                                    checked={agreementChecked}
                                    onChange={handleAgreementChange}
                                >
                                    {localize('familiar-accept', translations)}{' '}
                                    <a
                                        href="#"
                                        onClick={openModalLink}
                                    >
                                        {localize('user-agreement', translations)}
                                    </a>
                                </Checkbox>
                            </Form.Item>
                            <Form.Item
                                name="email"
                                rules={[
                                    {
                                        required: true,
                                        message: localize(
                                            'block-auth-required-field',
                                            translations
                                        ),
                                    },
                                    {
                                        type: 'email',
                                        message: localize(
                                            'block-auth-incorrect-email-format',
                                            translations
                                        ),
                                    },
                                ]}
                            >
                                <Input placeholder="Email" />
                            </Form.Item>

                            <Button
                                type="primary"
                                color="gold"
                                htmlType="submit"
                                loading={signing}
                                disabled={!agreementChecked}
                            >
                                {localize('block-auth-register', translations)}
                            </Button>
                        </Col>
                    ) : null}
                </Row>
            </Form>

            <Modal
                title={
                    <div style={{ textAlign: 'center' }}>
                        {localize('user-agreement', translations)}
                    </div>
                }
                open={modalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={1000}
            >
                <p>{localize('privacy-policy', translations)}</p>
                <p>{localize('personal-information', translations)}</p>
                <p>{localize('all-personal-information', translations)}</p>
                <p>{localize('provision-personal-data', translations)}</p>
                <p>{localize('referring-portal', translations)}</p>
                <p>{localize('provide-personal-data', translations)}</p>
                <div
                    className="modal__button-container"
                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                    <Button
                        type="primary"
                        color="gold"
                        onClick={handleAccept}
                    >
                        {localize('familiar-accept', translations)}
                    </Button>
                </div>
            </Modal>

            <RegVerificationModal
                open={openRegVerification}
                close={closeRegVerificationModal}
                onOk={handleRegVerification}
                translations={translations}
                form={formRegVerification}
            />
        </>
    );
};

export default SignUpUser;
